import { useApolloClient } from "@apollo/client";
import React from "react";
import { Props as TagsProps } from "src/components/Tags/TagsList";
import { TagsPopover } from "src/components/Tags/TagsPopover";
import { sleep } from "src/services/sleep";
import * as RemoteData from "src/types/remoteData";
import { GET_TAGS_FOR_FILTERS } from "../forms/graphql/queries";
import { SchoolFormId } from "../forms/types";
import { useTags } from "./useTags";

type Props = {
  enrollmentPeriodId: uuid;
  fetchSelectedFormKeys: () => Promise<SchoolFormId[]>;
  refetchForms: () => Promise<unknown>;
} & Omit<TagsProps, "tags" | "onSearch" | "onTagUpdate">;

export const BulkTagsPopover: React.FC<Props> = ({
  enrollmentPeriodId,
  fetchSelectedFormKeys,
  refetchForms,
  ...overrideTagsProps
}) => {
  const {
    setSelectedRows,
    tagsProps,
    isDirty,
    resetTags,
    setSelectedTagGroupId,
    tagGroups,
  } = useTags({
    enrollmentPeriodId,
    tagGroupId: null,
    isEditing: true,
  });

  const onOpen = React.useCallback(async () => {
    setSelectedRows(RemoteData.loading());

    await sleep(100);
    const ids = await fetchSelectedFormKeys();

    setSelectedRows(RemoteData.success(ids));
  }, [fetchSelectedFormKeys, setSelectedRows]);

  const client = useApolloClient();
  const onClose = React.useCallback(async () => {
    if (isDirty) {
      refetchForms();
      client.refetchQueries({
        include: [GET_TAGS_FOR_FILTERS],
      });
    }
    resetTags(true);
  }, [client, isDirty, refetchForms, resetTags]);

  return (
    <TagsPopover
      onOpen={onOpen}
      onClose={onClose}
      buttonProps={{ variant: "banner", size: "sm" }}
      onSelectTagGroup={setSelectedTagGroupId}
      isBulkAction={true}
      tagGroups={tagGroups}
      onBackClick={() => resetTags(true)}
      {...tagsProps}
      {...overrideTagsProps}
    />
  );
};
